<template>
  <div class="date-range">
    <div class="date-range__field">
      <label>С</label>
      <DxDateBox
        :value="from"
        type="datetime"
        display-format="dd.MM.yyyy HH:mm"
        width="200"
        :show-clear-button="true"
        @value-changed="onFrom"
      />
    </div>
    <div class="date-range__field">
      <label>По</label>
      <DxDateBox
        :value="to"
        type="datetime"
        display-format="dd.MM.yyyy HH:mm"
        width="200"
        :show-clear-button="true"
        @value-changed="onTo"
      />
    </div>
    <div class="date-range__presets">
      <DxDropDownButton
        :text="presetButtonText"
        icon="event"
        styling-mode="outlined"
        :items="presets"
        display-expr="text"
        key-expr="id"
        :use-select-mode="true"
        :selected-item-key="selectedPresetId"
        :drop-down-options="{ width: 140 }"
        @button-click="applySelectedPreset"
        @item-click="onPresetItemClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DxDateBox } from 'devextreme-vue/date-box';
import { DxDropDownButton } from 'devextreme-vue/drop-down-button';
import { defaultRangeDays } from '../utils/format';

type PresetId = 'all' | '1' | '7' | '30' | 'custom';

type Preset = {
  id: PresetId;
  text: string;
  days: number | null;
};

const props = defineProps<{
  from: Date | null;
  to: Date | null;
}>();

const emit = defineEmits<{
  (e: 'update:from', value: Date | null): void;
  (e: 'update:to', value: Date | null): void;
  (e: 'change', value: { from: Date | null; to: Date | null }): void;
}>();

const presets: Preset[] = [
  { id: 'all', text: 'Все', days: null },
  { id: '1', text: '24ч', days: 1 },
  { id: '7', text: '7д', days: 7 },
  { id: '30', text: '30д', days: 30 },
];

const selectedPresetId = ref<PresetId>('7');

const presetButtonText = computed(() => {
  if (selectedPresetId.value === 'custom') return 'Период';
  return presets.find((p) => p.id === selectedPresetId.value)?.text ?? 'Период';
});

function guessPreset(from: Date | null, to: Date | null): PresetId {
  if (from == null && to == null) return 'all';
  if (!from || !to) return 'custom';
  const diffMs = to.getTime() - from.getTime();
  if (diffMs <= 0) return 'custom';
  const days = diffMs / (24 * 60 * 60 * 1000);
  for (const p of presets) {
    if (p.days == null) continue;
    if (Math.abs(days - p.days) < 0.05) return p.id;
  }
  return 'custom';
}

watch(
  () => [props.from, props.to] as const,
  ([from, to]) => {
    selectedPresetId.value = guessPreset(from, to);
  },
  { immediate: true },
);

function emitRange(from: Date | null, to: Date | null) {
  emit('update:from', from);
  emit('update:to', to);
  emit('change', { from, to });
}

function applyPreset(preset: Preset) {
  selectedPresetId.value = preset.id;
  if (preset.days == null) {
    emitRange(null, null);
    return;
  }
  const { from, to } = defaultRangeDays(preset.days);
  emitRange(from, to);
}

function onPresetItemClick(e: { itemData?: Preset }) {
  const preset = e.itemData;
  if (!preset) return;
  applyPreset(preset);
}

function applySelectedPreset() {
  const preset = presets.find((p) => p.id === selectedPresetId.value);
  if (!preset || selectedPresetId.value === 'custom') return;
  applyPreset(preset);
}

function onFrom(e: { value?: Date | null; event?: Event }) {
  // Ignore DxDateBox init / rebind (no native event) — avoids reload storms on mount.
  if (!e.event) return;
  const from = e.value ?? null;
  selectedPresetId.value = guessPreset(from, props.to);
  emit('update:from', from);
  emit('change', { from, to: props.to });
}

function onTo(e: { value?: Date | null; event?: Event }) {
  if (!e.event) return;
  const to = e.value ?? null;
  selectedPresetId.value = guessPreset(props.from, to);
  emit('update:to', to);
  emit('change', { from: props.from, to });
}
</script>

<style scoped>
.date-range {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.date-range__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-range__field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.date-range__presets {
  display: flex;
  gap: 6px;
  padding-bottom: 2px;
}
</style>
